// 숫자 카운팅 애니메이션 (rAF, reduced-motion 존중)
import { useEffect, useRef, useState } from 'react'

export interface CounterProps {
  value: number
  duration?: number
  decimals?: number
  prefix?: string
  suffix?: string
  className?: string
}

export function Counter({
  value,
  duration = 1100,
  decimals = 0,
  prefix = '',
  suffix = '',
  className,
}: CounterProps) {
  const [display, setDisplay] = useState(0)
  const startRef = useRef<number | null>(null)
  const fromRef = useRef(0)
  const rafRef = useRef<number | null>(null)

  useEffect(() => {
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduced) {
      setDisplay(value)
      return
    }
    fromRef.current = display
    startRef.current = null
    const tick = (ts: number) => {
      if (startRef.current === null) startRef.current = ts
      const elapsed = ts - startRef.current
      const t = Math.min(1, elapsed / duration)
      const eased = 1 - Math.pow(1 - t, 3)
      const next = fromRef.current + (value - fromRef.current) * eased
      setDisplay(next)
      if (t < 1) rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration])

  const formatted = display.toLocaleString('ko-KR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })

  // suffix가 비ASCII 한 글자 단위(건/명/조/개/곳/회) 또는 % 면 시각적으로 약간 작고 톤다운.
  // 큰 숫자와 단위의 시각 위계 살리기 위함. aria-label은 풀텍스트로 유지.
  const suffixTrim = suffix.trimStart()
  const hasUnit = suffixTrim.length > 0 && /^[%가-힣A-Za-z]/.test(suffixTrim)
  return (
    <span className={`mono ${className ?? ''}`.trim()} aria-label={`${prefix}${formatted}${suffix}`}>
      {prefix}{formatted}
      {suffix && (hasUnit
        ? <span className="num-unit">{suffix}</span>
        : suffix)}
    </span>
  )
}
